import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

export const IPF_CATEGORY = {
  PRODUCTION: 'Production',
  SUPPLIER: 'Supplier',
  TRANSPORT: 'Transport',
  PROCESSING: 'Processing'
};

export const COST_SECTIONS = [
  { category: IPF_CATEGORY.PRODUCTION, title: 'Production' },
  { category: IPF_CATEGORY.SUPPLIER, title: 'Supplier' },
  { category: IPF_CATEGORY.TRANSPORT, title: 'Despatch - Transport' },
  { category: IPF_CATEGORY.PROCESSING, title: 'Processing / System' }
];

export function getWeekRange(date) {
  const start = dayjs(date, 'YYYY-MM-DD').startOf('isoWeek');
  const days = Array.from({ length: 6 }, (_, i) => start.add(i, 'day').format('YYYY-MM-DD'));
  return { startDate: days[0], endDate: days[days.length - 1], days };
}

export function frameKey(item) {
  return item.frameId ? `${item.ipfJobNo}-${item.frameId}` : null;
}

export function countUniqueFrames(items) {
  const frameKeys = new Set();
  let itemsWithoutFrame = 0;
  items.forEach(item => {
    const key = frameKey(item);
    if (key) {
      frameKeys.add(key);
    } else {
      itemsWithoutFrame += 1;
    }
  });
  return frameKeys.size + itemsWithoutFrame;
}

function sumIpfMaterialCost(items) {
  const seenIpfIds = new Set();
  return items.reduce((sum, r) => {
    if (r.ipfId != null) {
      if (seenIpfIds.has(r.ipfId)) return sum;
      seenIpfIds.add(r.ipfId);
    }
    return sum + (r.ipfMaterialCost || 0);
  }, 0);
}

export function buildCategoryRows(records, category, days) {
  const byCategory = records.filter(r => r.category === category);

  const rows = days.map(day => {
    const dayRecords = byCategory.filter(r => r.ipfDate && dayjs(r.ipfDate).format('YYYY-MM-DD') === day);
    const whiteItems = dayRecords.filter(r => !!r.isWhite);
    const foiledItems = dayRecords.filter(r => !r.isWhite);
    return {
      date: day,
      whiteCount: countUniqueFrames(whiteItems),
      whiteTotalCost: sumIpfMaterialCost(whiteItems),
      whiteItems,
      foiledCount: countUniqueFrames(foiledItems),
      foiledTotalCost: sumIpfMaterialCost(foiledItems),
      foiledItems
    };
  });

  const totalWhiteItems = rows.flatMap(r => r.whiteItems);
  const totalFoiledItems = rows.flatMap(r => r.foiledItems);

  const totals = {
    date: 'TOTAL',
    whiteCount: countUniqueFrames(totalWhiteItems),
    whiteTotalCost: sumIpfMaterialCost(totalWhiteItems),
    whiteItems: totalWhiteItems,
    foiledCount: countUniqueFrames(totalFoiledItems),
    foiledTotalCost: sumIpfMaterialCost(totalFoiledItems),
    foiledItems: totalFoiledItems
  };

  return { rows, totals };
}

export function buildFaultCodeCounts(records) {
  const byCode = {};
  records.forEach(r => {
    const code = r.pCode || 'Unknown';
    if (!byCode[code]) byCode[code] = [];
    byCode[code].push(r);
  });

  return Object.entries(byCode)
    .map(([code, items]) => ({
      code,
      count: countUniqueFrames(items),
      description: items.find(r => r.pCodeDescription)?.pCodeDescription || '',
      items
    }))
    .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
}

export function formatCost(value) {
  return `£${(value || 0).toFixed(2)}`;
}
